import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { cn } from '@/lib/utils';
import useGraphStore from '@/store/graphStore';
import { chainColors } from '@/data/blockchainMockData';

// Pure helper functions (no dependencies on component state)
const getNodeRadius = (node) => {
  const baseSize = 10;
  const balanceScale = Math.log(node.balance + 1) * 3;
  const degreeScale = Math.sqrt(node.degree) * 2;
  return Math.max(baseSize, Math.min(35, baseSize + balanceScale + degreeScale));
};

const getChainIcon = (chain) => {
  const icons = {
    BTC: '\uf15a',
    ETH: '\uf42e',
    SOL: '\uf185',
    MATIC: '\uf312',
    AVAX: '\uf6fc',
    BNB: '\uf51e',
  };
  return icons[chain] || '\uf0ac';
};

const pointInPolygon = (point, polygon) => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1];
    const xj = polygon[j][0], yj = polygon[j][1];
    if (((yi > point[1]) !== (yj > point[1])) && (point[0] < (xj - xi) * (point[1] - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
};

const ForceGraph = ({ width, height, className }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const simulationRef = useRef(null);
  const zoomRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [previewLine, setPreviewLine] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  const {
    filteredNodes,
    filteredEdges,
    layout,
    zoom,
    setZoom,
    pan,
    setPan,
    setSelectedNode,
    setHoveredNode,
    selectedNode,
    selectedNodes,
    toggleNodeSelection,
    selectByLasso,
    showBlockchainIcons,
    connectMode,
    connectSource,
    setConnectSource,
    neighborhoodMode,
    neighborhoodCenter,
    getNeighborhoodData,
    magnetMode,
  } = useGraphStore();

  // Get data based on mode
  const getData = useCallback(() => {
    if (neighborhoodMode && neighborhoodCenter) {
      return getNeighborhoodData();
    }
    return { nodes: filteredNodes, edges: filteredEdges };
  }, [neighborhoodMode, neighborhoodCenter, filteredNodes, filteredEdges, getNeighborhoodData]);

  // Programmatic zoom control
  const zoomTo = useCallback((scale) => {
    if (!svgRef.current || !zoomRef.current) return;
    
    const svg = d3.select(svgRef.current);
    const actualWidth = width || containerRef.current?.clientWidth || 800;
    const actualHeight = height || containerRef.current?.clientHeight || 600;
    
    svg.transition()
      .duration(300)
      .call(
        zoomRef.current.transform,
        d3.zoomIdentity
          .translate(actualWidth / 2, actualHeight / 2)
          .scale(scale)
          .translate(-actualWidth / 2, -actualHeight / 2)
      );
  }, [width, height]);

  // Fit view to content
  const fitView = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return;
    
    const svg = d3.select(svgRef.current);
    const g = svg.select('.graph-container');
    const bounds = g.node()?.getBBox();
    
    if (!bounds) return;
    
    const actualWidth = width || containerRef.current?.clientWidth || 800;
    const actualHeight = height || containerRef.current?.clientHeight || 600;
    
    const dx = bounds.width;
    const dy = bounds.height;
    const x = bounds.x + dx / 2;
    const y = bounds.y + dy / 2;
    
    const scale = Math.min(0.9 * actualWidth / dx, 0.9 * actualHeight / dy, 2);
    const translate = [actualWidth / 2 - scale * x, actualHeight / 2 - scale * y];
    
    svg.transition()
      .duration(500)
      .call(
        zoomRef.current.transform,
        d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale)
      );
  }, [width, height]);

  // Reset view
  const resetView = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return;
    
    const svg = d3.select(svgRef.current);
    
    svg.transition()
      .duration(300)
      .call(zoomRef.current.transform, d3.zoomIdentity);
    
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [setZoom, setPan]);

  // Expose zoom functions to store
  useEffect(() => {
    useGraphStore.setState({
      zoomIn: () => zoomTo(Math.min(zoom * 1.3, 3)),
      zoomOut: () => zoomTo(Math.max(zoom / 1.3, 0.5)),
      fitView,
      resetViewFn: resetView,
    });
  }, [zoom, zoomTo, fitView, resetView]);

  // SVG Defs for effects
  const createDefs = (svg) => {
    const defs = svg.append('defs');
    
    // Arrow markers
    ['inflow', 'outflow'].forEach(type => {
      defs.append('marker')
        .attr('id', `arrow-${type}`)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 20)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('fill', type === 'inflow' ? '#22c55e' : '#ef4444')
        .attr('d', 'M0,-5L10,0L0,5');
    });
    
    // Selection glow filter
    const selectionGlow = defs.append('filter')
      .attr('id', 'selection-glow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    
    selectionGlow.append('feGaussianBlur')
      .attr('stdDeviation', '4')
      .attr('result', 'coloredBlur');
    
    const feMerge = selectionGlow.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
    
    // Gradient for selection
    const gradient = defs.append('linearGradient')
      .attr('id', 'selection-gradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '100%');
    
    gradient.append('stop').attr('offset', '0%').attr('stop-color', '#3b82f6');
    gradient.append('stop').attr('offset', '100%').attr('stop-color', '#8b5cf6');
  };

  // Main graph initialization
  useEffect(() => {
    if (!svgRef.current) return;
    
    const { nodes: dataNodes, edges: dataEdges } = getData();
    if (dataNodes.length === 0) return;
    
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    
    const actualWidth = width || containerRef.current?.clientWidth || 800;
    const actualHeight = height || containerRef.current?.clientHeight || 600;
    
    createDefs(svg);

    // Local helper functions for this effect
    const createRipple = (g, nodeData) => {
      const ripple = g.append('circle')
        .attr('cx', nodeData.x).attr('cy', nodeData.y)
        .attr('r', getNodeRadius(nodeData))
        .attr('fill', 'none')
        .attr('stroke', '#3b82f6')
        .attr('stroke-width', 2)
        .attr('opacity', 1);

      ripple.transition().duration(500)
        .attr('r', getNodeRadius(nodeData) * 3)
        .attr('opacity', 0)
        .remove();
    };

    const updateSelectionVisuals = (nodeSelection, linkSelection) => {
      const selectedIds = new Set(selectedNodes.map(n => n.id));
      if (selectedNode) selectedIds.add(selectedNode.id);
      if (connectSource) selectedIds.add(connectSource.id);

      nodeSelection.select('.selection-ring')
        .transition().duration(200)
        .attr('opacity', d => selectedIds.has(d.id) ? 1 : 0)
        .attr('r', d => getNodeRadius(d) + (selectedIds.has(d.id) ? 10 : 8));

      nodeSelection.select('.node-circle')
        .transition().duration(200)
        .attr('transform', d => selectedIds.has(d.id) ? 'scale(1.2)' : 'scale(1)');

      if (selectedIds.size > 0) {
        linkSelection.transition().duration(200)
          .attr('stroke-opacity', d => {
            const sId = typeof d.source === 'string' ? d.source : d.source?.id;
            const tId = typeof d.target === 'string' ? d.target : d.target?.id;
            return selectedIds.has(sId) || selectedIds.has(tId) ? 1 : 0.2;
          });
      } else {
        linkSelection.transition().duration(200).attr('stroke-opacity', 0.6);
      }
    };

    // Create container group for zoom/pan
    const g = svg.append('g').attr('class', 'graph-container');
    
    // Preview line layer
    const previewLayer = svg.append('g').attr('class', 'preview-layer');
    
    // Lasso layer
    const lassoLayer = svg.append('g').attr('class', 'lasso-layer');
    
    // Prepare data
    const nodes = dataNodes.map(n => ({ 
      ...n,
      x: n.x || actualWidth / 2 + (Math.random() - 0.5) * 400,
      y: n.y || actualHeight / 2 + (Math.random() - 0.5) * 400,
    }));
    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    
    const links = dataEdges
      .filter(e => {
        const sourceId = typeof e.source === 'string' ? e.source : e.source?.id;
        const targetId = typeof e.target === 'string' ? e.target : e.target?.id;
        return nodeMap.has(sourceId) && nodeMap.has(targetId);
      })
      .map(e => {
        const sourceId = typeof e.source === 'string' ? e.source : e.source?.id;
        const targetId = typeof e.target === 'string' ? e.target : e.target?.id;
        return { ...e, source: nodeMap.get(sourceId), target: nodeMap.get(targetId) };
      });
    
    // Create simulation - only if magnetMode is enabled
    let simulation;
    
    if (magnetMode) {
      if (layout === 'force' || neighborhoodMode) {
        simulation = d3.forceSimulation(nodes)
          .force('link', d3.forceLink(links).id(d => d.id).distance(100))
          .force('charge', d3.forceManyBody().strength(-300))
          .force('center', d3.forceCenter(actualWidth / 2, actualHeight / 2))
          .force('collision', d3.forceCollide().radius(d => getNodeRadius(d) + 5));
      } else if (layout === 'circular') {
        const angleStep = (2 * Math.PI) / nodes.length;
        const radius = Math.min(actualWidth, actualHeight) / 3;
        nodes.forEach((node, i) => {
          node.x = actualWidth / 2 + radius * Math.cos(i * angleStep);
          node.y = actualHeight / 2 + radius * Math.sin(i * angleStep);
        });
        simulation = d3.forceSimulation(nodes).force('link', d3.forceLink(links).id(d => d.id).distance(50));
      } else {
        simulation = d3.forceSimulation(nodes)
          .force('link', d3.forceLink(links).id(d => d.id).distance(80))
          .force('charge', d3.forceManyBody().strength(-200))
          .force('y', d3.forceY(actualHeight / 2).strength(0.1))
          .force('x', d3.forceX(actualWidth / 2).strength(0.1));
      }
    } else {
      // No physics - just position nodes and stop
      simulation = d3.forceSimulation(nodes).stop();
    }
    
    simulationRef.current = simulation;
    
    // Create links
    const link = g.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', d => d.type === 'inflow' ? '#22c55e' : '#ef4444')
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', d => Math.max(1, Math.min(6, d.amount / 5)))
      .attr('stroke-dasharray', d => d.isUserCreated ? '5,5' : 'none')
      .attr('marker-end', d => `url(#arrow-${d.type})`)
      .attr('x1', d => d.source.x)
      .attr('y1', d => d.source.y)
      .attr('x2', d => d.target.x)
      .attr('y2', d => d.target.y);
    
    // Create node groups
    const node = g.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .attr('class', 'node-group cursor-pointer')
      .attr('data-testid', d => `node-${d.id}`)
      .attr('transform', d => `translate(${d.x},${d.y})`);
    
    // Selection ring
    node.append('circle')
      .attr('class', 'selection-ring')
      .attr('r', d => getNodeRadius(d) + 8)
      .attr('fill', 'none')
      .attr('stroke', 'url(#selection-gradient)')
      .attr('stroke-width', 3)
      .attr('opacity', 0)
      .style('filter', 'url(#selection-glow)');
    
    // Main node circle
    node.append('circle')
      .attr('class', 'node-circle')
      .attr('r', d => getNodeRadius(d))
      .attr('fill', d => d.color)
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 1.5)
      .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))');
    
    // Blockchain icon INSIDE node (centered, 60% size)
    if (showBlockchainIcons) {
      node.append('text')
        .attr('class', 'blockchain-icon')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('font-family', 'Font Awesome 6 Free, Font Awesome 6 Brands')
        .attr('font-size', d => getNodeRadius(d) * 0.6 * 2) // 60% of diameter
        .attr('fill', '#ffffff')
        .attr('opacity', 0.95)
        .attr('pointer-events', 'none')
        .style('transform-origin', 'center')
        .style('transition', 'transform 0.3s ease')
        .text(d => getChainIcon(d.chain));
    }
    
    // Node labels
    node.filter(d => d.degree > 3 || d.balance > 10)
      .append('text')
      .attr('dy', d => getNodeRadius(d) + 14)
      .attr('text-anchor', 'middle')
      .attr('fill', 'currentColor')
      .attr('font-size', '10px')
      .attr('class', 'pointer-events-none')
      .text(d => d.label.length > 12 ? d.label.slice(0, 12) + '...' : d.label);
    
    // Free drag behavior (no physics)
    const dragBehavior = d3.drag()
      .on('start', function(event, d) {
        d3.select(this).raise();
        if (magnetMode && simulation) {
          if (!event.active) simulation.alphaTarget(0.3).restart();
        }
      })
      .on('drag', function(event, d) {
        d.x = event.x;
        d.y = event.y;
        d3.select(this).attr('transform', `translate(${d.x},${d.y})`);
        
        // Update connected edges
        link
          .filter(l => l.source.id === d.id)
          .attr('x1', d.x)
          .attr('y1', d.y);
        link
          .filter(l => l.target.id === d.id)
          .attr('x2', d.x)
          .attr('y2', d.y);
        
        // Update preview line if in connect mode
        if (connectMode && connectSource?.id === d.id) {
          updatePreviewLine(d.x, d.y);
        }
      })
      .on('end', function(event, d) {
        if (magnetMode && simulation) {
          if (!event.active) simulation.alphaTarget(0);
        }
        // Node stays where dropped (free movement)
      });
    
    node.call(dragBehavior);
    
    // Click handlers
    node
      .on('click', (event, d) => {
        event.stopPropagation();
        
        if (connectMode) {
          if (!connectSource) {
            setConnectSource(d);
            showPreviewLine(d);
          } else if (connectSource.id !== d.id) {
            // Create edge
            const edgeEvent = new CustomEvent('createEdge', { 
              detail: { source: connectSource, target: d } 
            });
            window.dispatchEvent(edgeEvent);
            hidePreviewLine();
          }
          return;
        }
        
        createRipple(g, d);
        toggleNodeSelection(d, event.shiftKey);
      })
      .on('mouseenter', function(event, d) {
        setHoveredNode(d);
        
        // Scale up
        d3.select(this).select('.node-circle')
          .transition().duration(150)
          .attr('r', getNodeRadius(d) * 1.15);
        
        // Rotate icon on hover
        if (showBlockchainIcons) {
          d3.select(this).select('.blockchain-icon')
            .transition().duration(300)
            .style('transform', 'rotate(360deg)');
        }
      })
      .on('mouseleave', function(event, d) {
        setHoveredNode(null);
        
        d3.select(this).select('.node-circle')
          .transition().duration(150)
          .attr('r', getNodeRadius(d));
        
        if (showBlockchainIcons) {
          d3.select(this).select('.blockchain-icon')
            .transition().duration(300)
            .style('transform', 'rotate(0deg)');
        }
      });
    
    // Update selection visuals
    updateSelectionVisuals(node, link);
    
    // Background click
    svg.on('click', (event) => {
      if (event.target === svgRef.current) {
        useGraphStore.getState().clearSelection();
        if (connectMode) {
          setConnectSource(null);
          hidePreviewLine();
        }
      }
    });
    
    // Preview line functions
    function showPreviewLine(sourceNode) {
      previewLayer.selectAll('*').remove();
      previewLayer.append('line')
        .attr('class', 'preview-line')
        .attr('stroke', '#3b82f6')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '8,4')
        .attr('stroke-opacity', 0.8)
        .attr('x1', sourceNode.x)
        .attr('y1', sourceNode.y)
        .attr('x2', sourceNode.x)
        .attr('y2', sourceNode.y);
    }
    
    function updatePreviewLine(x, y) {
      previewLayer.select('.preview-line')
        .attr('x2', x)
        .attr('y2', y);
    }
    
    function hidePreviewLine() {
      previewLayer.selectAll('*').remove();
    }
    
    // Mouse move for preview line
    svg.on('mousemove', (event) => {
      if (connectMode && connectSource) {
        const [x, y] = d3.pointer(event, g.node());
        previewLayer.select('.preview-line')
          .attr('x2', x)
          .attr('y2', y);
      }
    });
    
    // Lasso selection
    let lassoPoints = [];
    let isLassoing = false;
    
    svg.on('mousedown', (event) => {
      if (event.shiftKey && !connectMode) {
        isLassoing = true;
        lassoPoints = [[event.offsetX, event.offsetY]];
      }
    });
    
    svg.on('mousemove.lasso', (event) => {
      if (isLassoing) {
        lassoPoints.push([event.offsetX, event.offsetY]);
        lassoLayer.selectAll('path').remove();
        lassoLayer.append('path')
          .attr('d', d3.line()(lassoPoints))
          .attr('fill', 'rgba(59, 130, 246, 0.1)')
          .attr('stroke', '#3b82f6')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '5,5');
      }
    });
    
    svg.on('mouseup', () => {
      if (isLassoing && lassoPoints.length > 2) {
        lassoPoints.push(lassoPoints[0]);
        const selectedIds = nodes.filter(n => pointInPolygon([n.x, n.y], lassoPoints)).map(n => n.id);
        if (selectedIds.length > 0) selectByLasso(selectedIds);
      }
      isLassoing = false;
      lassoPoints = [];
      lassoLayer.selectAll('*').remove();
    });
    
    // Zoom behavior
    const zoomBehavior = d3.zoom()
      .scaleExtent([0.5, 3])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
        previewLayer.attr('transform', event.transform);
        setZoom(event.transform.k);
        setPan({ x: event.transform.x, y: event.transform.y });
      });
    
    zoomRef.current = zoomBehavior;
    svg.call(zoomBehavior);
    
    // Apply initial transform
    if (pan.x !== 0 || pan.y !== 0 || zoom !== 1) {
      svg.call(zoomBehavior.transform, d3.zoomIdentity.translate(pan.x, pan.y).scale(zoom));
    }
    
    // Simulation tick (only if magnetMode)
    if (magnetMode && simulation) {
      simulation.on('tick', () => {
        link
          .attr('x1', d => d.source.x)
          .attr('y1', d => d.source.y)
          .attr('x2', d => d.target.x)
          .attr('y2', d => d.target.y);
        node.attr('transform', d => `translate(${d.x},${d.y})`);
      });
    }
    
    setIsInitialized(true);
    
    return () => {
      if (simulation) simulation.stop();
    };
  }, [filteredNodes, filteredEdges, layout, width, height, showBlockchainIcons, neighborhoodMode, neighborhoodCenter, connectMode, magnetMode, getData, connectSource, pan.x, pan.y, selectedNodes, selectedNode]);

  // Wrap updateSelectionVisuals in useCallback for stable reference
  const updateSelectionVisualsCallback = useCallback((node, link) => {
    const selectedIds = new Set(selectedNodes.map(n => n.id));
    if (selectedNode) selectedIds.add(selectedNode.id);
    if (connectSource) selectedIds.add(connectSource.id);

    node.select('.selection-ring')
      .transition().duration(200)
      .attr('opacity', d => selectedIds.has(d.id) ? 1 : 0)
      .attr('r', d => getNodeRadius(d) + (selectedIds.has(d.id) ? 10 : 8));

    node.select('.node-circle')
      .transition().duration(200)
      .attr('transform', d => selectedIds.has(d.id) ? 'scale(1.2)' : 'scale(1)');

    if (selectedIds.size > 0) {
      link.transition().duration(200)
        .attr('stroke-opacity', d => {
          const sId = typeof d.source === 'string' ? d.source : d.source?.id;
          const tId = typeof d.target === 'string' ? d.target : d.target?.id;
          return selectedIds.has(sId) || selectedIds.has(tId) ? 1 : 0.2;
        });
    } else {
      link.transition().duration(200).attr('stroke-opacity', 0.6);
    }
  }, [selectedNodes, selectedNode, connectSource]);

  // Update selection visuals
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const node = svg.selectAll('.node-group');
    const link = svg.selectAll('.links line');
    updateSelectionVisualsCallback(node, link);
  }, [selectedNodes, selectedNode, connectSource, updateSelectionVisualsCallback]);

  return (
    <div ref={containerRef} className={cn("w-full h-full relative", className)}>
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        className="bg-background"
        style={{ touchAction: 'none' }}
        aria-label="Blockchain funds flow graph"
        role="img"
      />
      
      {connectMode && connectSource && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-medium animate-pulse z-20">
          Click another node to connect
        </div>
      )}
    </div>
  );
};

export default ForceGraph;
